import type { KeyAndInnerKeys } from "@shared/frontend/hyper-table/types";
import { getChildEntity } from "@shared/helpers/sql-relations";
import { getValueList, repo, type ClassType } from "remult";
import type {
  ActiveFilter,
  HyperTableEntityFilter,
  HyperTableFilterBoth,
} from "../../../filter-types";

export function getFinalChildEntityFromFieldPath<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
) {
  const p = field.split(".");
  p.pop();
  return p.reduce((p, c) => getChildEntity(p, c as any), entity);
}

export function isFieldLiteral<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
) {
  return !!getValueList(
    repo(getFinalChildEntityFromFieldPath(entity, field)).metadata.fields.find(
      field.split(".").pop()!,
    ),
  );
}

export function isFieldBoolean<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
) {
  return getFieldType(entity, field) === Boolean;
}

export function isFieldDate<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
) {
  return getFieldType(entity, field) === Date;
}

export function isFieldNumber<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
) {
  return getFieldType(entity, field) === Number;
}

export function isFieldString<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
) {
  return (
    getFieldType(entity, field) === String && !isFieldLiteral(entity, field) //Literal fields also have type String but are not string fields
  );
}

export function getFieldType<Entity>(
  entity: ClassType<Entity>,
  field: KeyAndInnerKeys<Entity>,
) {
  return repo(
    getFinalChildEntityFromFieldPath(entity, field),
  ).metadata.fields.find(field.split(".").pop() as any)?.valueType;
}

export interface EntityFilterProps<Entity> {
  filter: HyperTableEntityFilter<Entity> &
  HyperTableFilterBoth<Entity> &
  ActiveFilter<Entity>;

  closeMenu: VoidFunction;
}

export const SELECT_OPERATORS = {
  string: [
    {
      caption: "מכיל",
      value: "contains",
      process: (val: string) => ({ $contains: val }),
    },
    {
      caption: "אינו מכיל",
      value: "notContains",
      process: (val: string) => ({ $notContains: val }),
    },
    {
      caption: "שווה",
      value: "equal",
      process: (val: string) => val,
    },
    {
      caption: "אינו שווה",
      value: "notEqual",
      process: (val: string) => ({ $ne: val }),
    },
  ],
  number: [
    {
      caption: "גדול או שווה",
      value: "greaterOrEqual",
      process: (val: number) => ({ $gte: val }),
    },
    {
      caption: "קטן או שווה",
      value: "lowerOrEqual",
      process: (val: number) => ({ $lte: val }),
    },
    {
      caption: "שווה",
      value: "equal",
      process: (val: number) => val,
    },
    {
      caption: "אינו שווה",
      value: "notEqual",
      process: (val: number) => ({ $ne: val }),
    },
  ],
  date: [
    {
      caption: "מאוחר או שווה",
      value: "greaterOrEqual",
      process: (val: Date) => ({
        $gte: (val.setHours(0, 0, 0, 0), val.toISOString()),
      }),
    },
    {
      caption: "מוקדם או שווה",
      value: "lowerOrEqual",
      process: (val: Date) => ({
        $lte: (val.setHours(0, 0, 0, 0), val.toISOString()),
      }),
    },
    {
      caption: "שווה",
      value: "equal",
      process: (val: Date) => ({
        $gte: (val.setHours(0, 0, 0, 0), val.toISOString()),
        $lte: (val.setHours(23, 59, 59, 999), val.toISOString()),
      }),
    },
    {
      caption: "אינו שווה",
      value: "notEqual",
      process: (val: Date) => ({
        $ne: (val.setHours(0, 0, 0, 0), val.toISOString()),
      }),
    },
  ],
} as const;

export const SELECT_OPERATORS_VALUES = Object.values(SELECT_OPERATORS).flatMap(
  (v) => v.flatMap((v) => v.value),
);

export type SelectOperatorValue = (typeof SELECT_OPERATORS_VALUES)[number];

export type SelectOperatorValueFor<K extends keyof typeof SELECT_OPERATORS> =
  (typeof SELECT_OPERATORS)[K][number]["value"];

export function getProcess<T extends keyof typeof SELECT_OPERATORS>(
  to: T,
  value: string | undefined,
) {
  const process = SELECT_OPERATORS[to].find((v) => v.value === value)?.process;
  if (!process)
    throw new Error(`process is undefined in string filter for value ${value}`);
  return process as (typeof SELECT_OPERATORS)[T][number]["process"];
}
